import { useEffect, useState } from "react";
import { getEstadosFincas, getServicesPropsGroup, getEstadosProveedores } from "../services";

/**
 * Hook que gestiona el almacenamiento en caché de states y groups en localStorage.
 * Carga los datos una sola vez y los mantiene en caché mientras el usuario esté autenticado.
 *
 * Casos de uso:
 * - Primera carga: carga datos del servidor y los almacena
 * - Navegación entre páginas: usa datos del caché
 * - Logout: se limpian automáticamente
 * - Recarga de página: si existen en localStorage, los usa; si no, recarga del servidor
 * - Cambio de versión: invalida la caché y recarga del servidor
 */

// Incrementar este número cuando se modifiquen datos maestros (estados, grupos, etc.)
const CACHE_VERSION = 2;

const parseCachedList = (raw) => {
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed) || parsed.length === 0) return null;
        return parsed;
    } catch {
        return null;
    }
};

const clearCache = () => {
    localStorage.removeItem("states");
    localStorage.removeItem("groups");
    localStorage.removeItem("dataCacheVersion");
    localStorage.removeItem("statesSupps");
};

const useDataCache = () => {
    const [states, setStates] = useState(null);
    const [groups, setGroups] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statesSupps, setStatesSupps] = useState(null);

    const fetchAndPersist = async () => {
        // Promise.allSettled: el fallo de una llamada no aborta las demás.
        const [statesResult, groupsResult, statesSuppsResult] = await Promise.allSettled([
            getEstadosFincas(),
            getServicesPropsGroup(),
            getEstadosProveedores()
        ]);
        const toFormatted = (result) => {
            const value = result.status === "fulfilled" ? result.value : null;
            return Array.isArray(value)
                ? value.map(item => ({ id: item.id, nombre: item.nombre, permiteStandBy: item?.permiteStandBy }))
                : [];
        };

        const formattedStates = toFormatted(statesResult);
        const formattedGroups = toFormatted(groupsResult);
        const formattedStatesSupps = toFormatted(statesSuppsResult);

        // Las listas críticas son states + groups. statesSupps es secundaria:
        // se persiste si llega, pero su ausencia no invalida toda la caché.
        if (formattedStates.length > 0 && formattedGroups.length > 0) {
            localStorage.setItem("states", JSON.stringify(formattedStates));
            localStorage.setItem("groups", JSON.stringify(formattedGroups));
            localStorage.setItem("dataCacheVersion", String(CACHE_VERSION));
            if (formattedStatesSupps.length > 0) {
                localStorage.setItem("statesSupps", JSON.stringify(formattedStatesSupps));
            }
            // Avisar a otros componentes (filtros, listados) que leen localStorage directamente
            // para que puedan refrescarse sin esperar a desmontarse/remontarse.
            window.dispatchEvent(new CustomEvent("dataCache:updated", {
                detail: { states: formattedStates, groups: formattedGroups, statesSupps: formattedStatesSupps }
            }));
        } else {
            clearCache();
        }

        return { formattedStates, formattedGroups, formattedStatesSupps };
    };

    useEffect(() => {
        let cancelled = false;

        const initializeCache = async () => {
            try {
                setLoading(true);

                const cachedVersion = localStorage.getItem("dataCacheVersion");
                const isVersionValid = cachedVersion && parseInt(cachedVersion) === CACHE_VERSION;

                const cachedStates = parseCachedList(localStorage.getItem("states"));
                const cachedGroups = parseCachedList(localStorage.getItem("groups"));
                const cachedStatesSupps = parseCachedList(localStorage.getItem("statesSupps"));

                // Caché válida = versión correcta + ambas listas son arrays NO vacíos
                if (cachedStates && cachedGroups && cachedStatesSupps && isVersionValid) {
                    if (cancelled) return;
                    setStates(cachedStates);
                    setGroups(cachedGroups);
                    setStatesSupps(cachedStatesSupps);
                    setLoading(false);
                    return;
                }


                if (!localStorage.getItem("token")) {
                    if (!cancelled) setLoading(false);
                    return;
                }

                // Caché ausente o corrupta (incluye el caso "[]" envenenado): limpiar y recargar
                clearCache();
                const { formattedStates, formattedGroups, formattedStatesSupps } = await fetchAndPersist();
                if (cancelled) return;
                setStates(formattedStates);
                setGroups(formattedGroups);
                setStatesSupps(formattedStatesSupps);
                setLoading(false);

            } catch (err) {
                console.error("Error loading data cache:", err);
                if (cancelled) return;
                setError(err);
                setLoading(false);
            }
        };

        initializeCache();

        const handleStorage = () => {
            if (!localStorage.getItem("token")) return;
            const yaCacheado = parseCachedList(localStorage.getItem("states"))
                && parseCachedList(localStorage.getItem("groups")) 
                && parseCachedList(localStorage.getItem("statesSupps"));
            if (yaCacheado) return;
            initializeCache();
        };
        window.addEventListener("storage", handleStorage);

        return () => {
            cancelled = true;
            window.removeEventListener("storage", handleStorage);
        };
    }, []);

    return {
        states,
        groups,
        statesSupps,
        loading,
        error,
        refetch: async () => {
            try {
                setLoading(true);
                if (!localStorage.getItem("token")) {
                    setLoading(false);
                    return;
                }
                const { formattedStates, formattedGroups, formattedStatesSupps } = await fetchAndPersist();
                setStates(formattedStates);
                setGroups(formattedGroups);
                setStatesSupps(formattedStatesSupps);
                setLoading(false);
            } catch (err) {
                console.error("Error refetching data cache:", err);
                setError(err);
                setLoading(false);
            }
        }
    };
};

export default useDataCache;
